<script setup lang="ts">
import { computed } from 'vue'
import type { Component } from 'vue'

import { IMPACT_TYPE_INACTIVE_PIN_COLOR_MAP, IMPACT_TYPE_MAP_PIN_COLOR_MAP } from '@/constants'
import type { ImpactType } from '@/api/interfaces'
import type { IMapPin } from '@/helpers/interfaces'

import CarbonPinPath from '@/components/icons/map-pins/CarbonPinPath.vue'
import KelpPinPath from '@/components/icons/map-pins/KelpPinPath.vue'
import PlasticPinPath from '@/components/icons/map-pins/PlasticPinPath.vue'
import TreesPinPath from '@/components/icons/map-pins/TreesPinPath.vue'
import WaterPinPath from '@/components/icons/map-pins/WaterPinPath.vue'
import BeesPinPath from '@/components/icons/map-pins/BeesPinPath.vue'

const impactIcons: Record<ImpactType, Component> = {
  carbon: CarbonPinPath,
  trees: TreesPinPath,
  plastic: PlasticPinPath,
  kelp: KelpPinPath,
  water: WaterPinPath,
  bees: BeesPinPath,
}

interface Props {
  pin: IMapPin
  isActive?: boolean
  isAnimated?: boolean
  isStatic?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isActive: false,
  isAnimated: true,
  isStatic: false,
})

const colorSettings = computed(() =>
  props.isActive ? IMPACT_TYPE_MAP_PIN_COLOR_MAP : IMPACT_TYPE_INACTIVE_PIN_COLOR_MAP,
)

const componentForImpactType = computed(() => impactIcons[props.pin.type])
const pinId = computed(
  () =>
    `public-map-pin-${formatCountryName(props.pin.country || props.pin.projectId)}-${props.pin.type}-${props.pin.index}${props.isStatic ? 'static' : ''}`,
)

const formatCountryName = (country: string): string =>
  country
    .trim()
    .toLowerCase()
    .replace(/\s*-\s*/g, '-')
    .replace(/\s+/g, '-')
</script>

<template>
  <svg
    :id="pinId"
    :class="[
      'public-map-pin',
      { 'is-active': isActive },
      { 'is-animated': isAnimated },
      { 'is-static': isStatic },
    ]"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 53 55"
    fill="none"
  >
    <g clip-path="url(#clip0_4558_28673)">
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M26.5 3C35.6093 3 46 9.94588 46 23.1882C46 31.9976 39.5093 41.2871 26.5 51C13.4907 41.2871 7 31.9976 7 23.1882C7 9.94588 17.3907 3 26.5 3Z"
        :fill="colorSettings[pin.type]"
      />
      <component :is="componentForImpactType" />
    </g>
  </svg>
</template>

<style scoped lang="scss">
.public-map-pin {
  position: absolute;
  opacity: 0;
  height: 30px;
  width: 30px;
  animation-fill-mode: forwards;
  transition: all 0.2s ease-in-out;
  cursor: pointer;
  z-index: 1;

  &.is-static {
    cursor: auto;
    position: relative;
    opacity: 1;
  }

  @include media('sm-and-up') {
    height: 45px;
    width: 45px;
  }

  &.is-active {
    width: 35px;
    height: 35px;
    z-index: 10;

    @include media('sm-and-up') {
      height: 50px;
      width: 50px;
    }
  }

  &.is-animated {
    animation-name: drop-in;
    animation-duration: 1s;
  }

  &:nth-child(1) {
    animation-delay: 0.1s;
  }

  &:nth-child(2) {
    animation-delay: 0.2s;
  }

  &:nth-child(3) {
    animation-delay: 0.3s;
  }
}

@keyframes drop-in {
  0% {
    transform: translateY(-150px);
  }

  100% {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
